import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BUILD_DATE, BUILD_TIME } from '@/version';

/**
 * VisitorFooter - Footer component for Visitor Mode (unauthenticated marketing pages)
 * Used on: Landing Page, About, Pricing, FAQ, Contact pages
 * 
 * Phase 1 Implementation: Marketing-focused footer with no app-level links
 */
const VisitorFooter = () => {
  const location = useLocation();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12">
          {/* Logo and Simple Link List */}
          <div className="flex flex-col items-center text-center space-y-6">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-16 w-auto rounded-lg"
            />
            
            {/* Simple Footer Navigation - Marketing Only */}
            <nav className="flex flex-wrap justify-center gap-6 text-sm">
              {[
                { label: 'About', path: '/about' },
                { label: 'Pricing', path: '/pricing' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Contact', path: '/contact' },
                { label: 'Our Authors', path: '/authors' }
              ]
                .filter(item => location.pathname !== item.path) // Hide current page
                .map((item) => (
                  <Link 
                    key={item.path}
                    to={item.path} 
                    className="text-slate-400 hover:text-slate-100 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))
              }
            </nav>
            
            <p className="text-sm text-slate-400 leading-relaxed max-w-xl">
              The future of books. Powered by engagement, built for authors and readers.
            </p>
          </div>
        </div>

        {/* Pre-MVP Disclaimer Button */}
        <div className="flex justify-center pb-6">
          <Button
            onClick={() => setShowDisclaimerModal(true)}
            variant="ghost"
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-sm font-medium"
            data-testid="pre-mvp-disclaimer-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Pre-MVP Release Disclaimer
          </Button>
        </div>

        {/* Powered by Emergent */}
        <div className="border-t border-slate-800 py-8">
          <div className="flex justify-center">
            <a 
              href="https://app.emergent.sh/register?ref=rdbr654428"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block group"
            >
              <img 
                src="/emergent-logo.png" 
                alt="Emergent.sh" 
                className="h-auto w-[200px] mx-auto mb-2 transition-transform group-hover:scale-105"
              />
              <p className="text-sm font-semibold text-slate-300 group-hover:text-purple-400 transition-colors text-center">
                Powered By Emergent.sh
              </p>
              <p className="text-xs text-slate-400 group-hover:text-purple-300 transition-colors text-center">
                Build Your Tomorrow Today!
              </p>
            </a>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-slate-800 py-6">
          <div className="flex flex-col items-center text-center space-y-2">
            <p className="text-sm text-slate-400">
              Copyright © 2026 NewAudia<sup className="font-bold text-[0.6em]">TM</sup> Systems, LLC
            </p>
            <a 
              href="https://www.newaudia.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              www.NewAudia.com
            </a>
            {/* Version/Build Info */}
            <p className="text-xs text-slate-500 mt-2" data-testid="build-version">
              NFD-SmartBooks™ - Rev. {BUILD_DATE} - {BUILD_TIME} MST
            </p>
          </div>
        </div>
      </div>

      {/* Pre-MVP Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader className="border-b border-slate-700 pb-4">
            <DialogTitle className="text-2xl font-bold text-amber-400 flex items-center gap-3">
              <FileText className="w-7 h-7" />
              Pre-Launch / MVP Status Disclaimer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4 text-slate-300 text-sm leading-relaxed">
            <div className="flex gap-4 text-xs text-slate-400">
              <span><strong>Effective Date:</strong> January 6, 2026</span>
              <span><strong>Last Updated:</strong> January 6, 2026</span>
            </div>
            
            <p>
              <strong className="text-white">NewAudia</strong> is currently operating as a <strong className="text-amber-400">pre-launch Minimum Viable Product (MVP)</strong>.
            </p>
            <p>
              While the platform is <strong className="text-white">operationally ready for public use</strong>, certain features, workflows, and performance characteristics may continue to evolve as we refine the system based on real-world usage.
            </p>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">1. Operational Readiness and Stability</h3>
              <p className="mb-3">
                The NewAudia platform has been designed and deployed with production-grade infrastructure and security controls. We do <strong className="text-white">not</strong> anticipate instability, data loss, or systemic outages as part of normal operation.
              </p>
              <p className="mb-2">However, as with any early-stage platform:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Minor glitches, inconsistencies, or unexpected behaviors may occur</li>
                <li>Feature interactions may evolve as usage patterns emerge</li>
                <li>Performance tuning and optimization are ongoing</li>
              </ul>
              <p className="mt-3 text-slate-400 italic">
                These conditions are inherent to iterative development and do not reflect a lack of platform integrity or long-term reliability.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">2. Feature Availability and Behavior</h3>
              <p className="mb-2">During the pre-launch phase:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Certain features may be modified, enhanced, or deprecated</li>
                <li>User interfaces and workflows may be refined</li>
                <li>Performance characteristics may change as capacity scales</li>
              </ul>
              <p className="mt-3">
                We reserve the right to make adjustments without prior notice in order to improve the overall experience and reliability of the Services.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">3. No Impact to Core Stability</h3>
              <p className="mb-3">
                Any identified issues during this phase are expected to be <strong className="text-white">non-catastrophic</strong> and <strong className="text-white">non-destructive</strong> in nature.
              </p>
              <p className="mb-2">We actively monitor system health and prioritize:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Data integrity</li>
                <li>Account security</li>
                <li>Transaction accuracy</li>
                <li>Platform availability</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">4. User Acknowledgment</h3>
              <p className="mb-2">By accessing or using the NewAudia platform during this phase, you acknowledge that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>You are participating in an early release environment</li>
                <li>Certain behaviors may differ from a fully matured release</li>
                <li>Continued improvements are part of the product lifecycle</li>
              </ul>
              <p className="mt-3">
                Your use of the Services remains subject to our <strong className="text-white">Terms of Service</strong>, <strong className="text-white">Privacy Policy</strong>, and all applicable platform policies.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">5. Feedback and Continuous Improvement</h3>
              <p className="mb-3">We welcome and encourage constructive feedback from early users.</p>
              <p className="mb-2">Your insights help us:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Identify edge cases and usability gaps</li>
                <li>Improve performance and reliability</li>
                <li>Prioritize enhancements that matter most to real users</li>
              </ul>
              <p className="mt-3 mb-2">Feedback may be submitted through:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>In-app feedback tools (where available)</li>
                <li>Direct communication with our team</li>
                <li>Designated support or feedback channels</li>
              </ul>
              <p className="mt-3 text-slate-400 italic">
                Providing feedback does not grant any ownership rights or expectations of compensation, but it does contribute meaningfully to the evolution of the platform.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-white mb-2">6. Forward-Looking Development</h3>
              <p>
                This pre-launch phase represents a deliberate step toward a broader public release. Improvements will be delivered iteratively, with the goal of building a durable, scalable, and user-centered platform.
              </p>
              <p className="mt-3">
                We appreciate your participation, patience, and trust as we continue to refine NewAudia.
              </p>
            </section>

            <section className="border-t border-slate-700 pt-4 mt-6">
              <h3 className="text-lg font-semibold text-white mb-2">Entity Identification</h3>
              <p className="text-slate-400">
                NewAudia is a business name (&quot;dba&quot;) of <strong className="text-white">Use What You Got Publishing, LLC</strong>, a Colorado limited liability company. Platform operations may transition to a successor entity, including <strong className="text-white">NewAudia Systems, LLC</strong>, without interruption to user rights or protections.
              </p>
              <p className="mt-4 text-center text-slate-500">
                Copyright © 2026 NewAudia<sup className="text-[0.6em]">TM</sup> Systems, LLC
              </p>
            </section>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-700">
            <Button
              onClick={() => setShowDisclaimerModal(false)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="close-disclaimer-btn"
            >
              I Understand
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default VisitorFooter;
